import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, FieldLabel } from '../internal';
import { Radio } from '../radio';

import type { ForwardedRef, ReactElement } from 'react';

import type { RadioProps } from '../radio';
import type { RadioFieldProps } from './radio-field.types';

/**
 * Radio fields are used to pick an option when only one option can be chosen.
 * A single Radio field can be used representing a singular option,
 * for the purpose of selecting an element between a group of options.
 *
 * ### Usage
 *
 * ```tsx
 * import { useState } from 'react';
 * import { RadioField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyRadioFieldComponent() {
 *   const [checked, setChecked] = useState(false);
 *
 *   return (
 *     <RadioField
 *       checked={checked}
 *       label="Radio button field with a label"
 *       onChange={(event) => setChecked(event.target.checked)}
 *     />
 *   );
 * }
 * ```
 */

const RadioField = forwardRef(
  (props: RadioFieldProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
    const {
      checked,
      className,
      defaultChecked,
      disabled = false,
      inputProps,
      label,
      name,
      onChange,
      readOnly = false,
      required = false,
      tooltip,
      value,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const controlId = useFieldId(inputProps?.id, 'rbui-radio-field');

    const radioProps: RadioProps = {
      checked,
      defaultChecked,
      disabled,
      inputProps: {
        ...inputProps,
        id: controlId,
      },
      name,
      onChange,
      readOnly,
      required,
      value,
    };

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip} view="normal">
        {label}
      </FieldLabel>
    );

    return (
      <BaseField
        className={cn('rbui-radio-field', className)}
        control={<Radio {...radioProps} ref={forwardedRef} />}
        label={fieldLabel}
        labelPosition="inline-end"
        {...rest}
      />
    );
  },
);

RadioField.displayName = 'RadioField';
export default RadioField;
